"use client";

import { TimeSlotCard } from "../unifiedtime/TimeSlotCard.js";
import { Clock } from "lucide-react";
import { parseDateString, getDayName } from "../../lib/date-utils.js";

// Public booking specific types
interface TimeSlot {
  start: string;
  end: string;
  isLunchSlot?: boolean;
}

interface AvailableDay {
  date: string;
  slots: TimeSlot[];
}

interface AvailabilityGridProps {
  availableDays: AvailableDay[];
  selectedSlot: string | null;
  onSlotSelect: (slot: TimeSlot) => void;
  bookeeEmail: string;
}

export function AvailabilityGrid({ 
  availableDays, 
  selectedSlot, 
  onSlotSelect, 
  bookeeEmail 
}: AvailabilityGridProps) {
  if (!availableDays || availableDays.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="h-16 w-16 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-semibold text-card-foreground mb-2">No Available Times</h3>
        <p className="text-gray-600 mb-4">
          No available slots found for this week.
        </p>
        <p className="text-sm text-gray-500">
          Try selecting a different week or contact {bookeeEmail} directly.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-2">
          Available Times for {bookeeEmail.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </h3>
        <p className="text-gray-600">
          Select a time that works for you
        </p>
      </div>
      
      <div className="space-y-6">
        {availableDays.map((day) => (
          <div key={day.date} className="space-y-3">
            <div className="border-b border-gray-200 pb-2">
              <h4 className="text-md font-medium text-card-foreground">
                {parseDateString(day.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h4>
              <p className="text-sm text-gray-500">
                {day.slots.length} available time{day.slots.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {day.slots.map((slot, index) => (
                <TimeSlotCard
                  key={`${day.date}-${index}`}
                  slot={slot}
                  isSelected={selectedSlot === slot.start}
                  onClick={onSlotSelect}
                  date={day.date}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
